/**
 * Connector OAuth Popup Utility
 *
 * Handles OAuth flow in a popup window with message passing back to parent.
 *
 * GitHub and GitLab set COOP: same-origin on their OAuth pages, which severs
 * the browsing context group. This breaks storage events, BroadcastChannel,
 * postMessage, and window.close() inside the popup. To work around this, we
 * add a proactive localStorage poll (Channel 5) that reads localStorage directly
 * without relying on events — localStorage is per-origin and immune to COOP.
 * The parent can still call popup.close() on its own popup even after COOP.
 *
 * Additional issue — Storage Partitioning (Chrome 115+, Firefox 103+):
 * When OptiDev runs as a cross-origin iframe (e.g., embedded in OptiSigns),
 * the iframe reads partitioned localStorage (keyed by top-level origin) while
 * popups opened by that iframe use unpartitioned localStorage (they are top-level
 * windows). These are different storage buckets — Channels 1, 3, and 5 all fail.
 * Channel 4 (popup.closed poll) still fires, but localStorage is empty during
 * the grace period. Pass fallbackCheck in OAuthOptions to verify via an external
 * source of truth (e.g., the connections API) instead.
 *
 * Channels used:
 * 1. storage event (fires instantly — may fail under COOP or storage partitioning)
 * 2. postMessage (works when window.opener preserved — may fail under COOP)
 * 3. BroadcastChannel (same browsing context group — may fail under COOP or storage partitioning)
 * 4. Poll timer with grace period (popup.closed — always fires, then uses fallbackCheck if provided)
 * 5. Proactive localStorage poll (immune to COOP — fails when storage is partitioned)
 */

export interface OAuthResult {
  success: boolean;
  connectionId?: string;
  connectorId?: string;
  connectorName?: string;
  error?: string;
  reauthorized?: boolean;
}

/**
 * Options for openOAuthPopup.
 * fallbackCheck: called when popup closes and localStorage is empty (storage
 * partitioning in embedded contexts). Return an OAuthResult if OAuth completed,
 * or null to treat as user-cancel.
 */
export interface OAuthOptions {
  fallbackCheck?: () => Promise<OAuthResult | null>;
}

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;
const OAUTH_POPUP_NAME = 'connector-oauth';
const OAUTH_RESULT_STORAGE_KEY = 'connector-oauth-result';

/**
 * Open OAuth flow in a popup window
 * Returns a promise that resolves when the popup completes (success or error)
 */
export function openOAuthPopup(authUrl: string, options?: OAuthOptions): Promise<OAuthResult> {
  return new Promise((resolve) => {
    let resolved = false;
    const resolveOnce = (result: OAuthResult): void => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve(result);
    };

    const left = window.screen.width / 2 - POPUP_WIDTH / 2;
    const top = window.screen.height / 2 - POPUP_HEIGHT / 2;

    const popup = window.open(
      authUrl,
      OAUTH_POPUP_NAME,
      `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},toolbar=no,location=yes,status=no,menubar=no,scrollbars=yes`,
    );

    if (!popup) {
      resolve({
        success: false,
        error: 'Popup blocked. Please allow popups for this site.',
      });
      return;
    }

    let pollTimer: ReturnType<typeof setInterval>;
    let graceTimer: ReturnType<typeof setTimeout>;
    let timeoutTimer: ReturnType<typeof setTimeout>;
    let localStoragePollTimer: ReturnType<typeof setInterval>;
    let bc: BroadcastChannel | null = null;

    const cleanup = (): void => {
      clearInterval(pollTimer);
      clearTimeout(graceTimer);
      clearTimeout(timeoutTimer);
      clearInterval(localStoragePollTimer);
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
      bc?.close();
      if (popup && !popup.closed) {
        popup.close();
      }
    };

    const tryReadLocalStorage = (): OAuthResult | null => {
      try {
        const stored = localStorage.getItem(OAUTH_RESULT_STORAGE_KEY);
        if (stored) {
          localStorage.removeItem(OAUTH_RESULT_STORAGE_KEY);
          return JSON.parse(stored) as OAuthResult;
        }
      } catch {
        // ignore parse errors
      }
      return null;
    };

    // Channel 1: storage event (fires instantly when popup writes to localStorage)
    const handleStorage = (event: StorageEvent): void => {
      if (event.key === OAUTH_RESULT_STORAGE_KEY && event.newValue) {
        try {
          const result = JSON.parse(event.newValue) as OAuthResult;
          localStorage.removeItem(OAUTH_RESULT_STORAGE_KEY);
          resolveOnce(result);
        } catch {
          // ignore parse errors
        }
      }
    };
    window.addEventListener('storage', handleStorage);

    // Channel 2: postMessage (works when window.opener preserved)
    const handleMessage = (event: MessageEvent): void => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'CONNECTOR_OAUTH_RESULT') {
        resolveOnce(event.data.payload as OAuthResult);
      }
    };
    window.addEventListener('message', handleMessage);

    // Channel 3: BroadcastChannel (same browsing context group)
    try {
      bc = new BroadcastChannel(OAUTH_POPUP_NAME);
      bc.onmessage = (event) => {
        if (event.data?.type === 'CONNECTOR_OAUTH_RESULT') {
          resolveOnce(event.data.payload as OAuthResult);
        }
      };
    } catch {
      // BroadcastChannel not supported in some browsers
    }

    // Clear any stale result from previous OAuth attempts
    localStorage.removeItem(OAUTH_RESULT_STORAGE_KEY);

    // Channel 4: Poll for popup close with grace period.
    // Cross-origin redirects (e.g., Shopify) can cause popup.closed to
    // briefly flicker true during navigation. The grace period ensures
    // we don't give up before the callback page loads and sends results.
    const GRACE_PERIOD_MS = 3000;
    const GRACE_CHECK_INTERVAL_MS = 300;

    pollTimer = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollTimer);

        const immediate = tryReadLocalStorage();
        if (immediate) {
          resolveOnce(immediate);
          return;
        }

        let elapsed = 0;
        graceTimer = setInterval(() => {
          elapsed += GRACE_CHECK_INTERVAL_MS;
          const result = tryReadLocalStorage();
          if (result) {
            clearInterval(graceTimer);
            resolveOnce(result);
            return;
          }
          if (elapsed >= GRACE_PERIOD_MS) {
            clearInterval(graceTimer);
            if (options?.fallbackCheck) {
              // Storage partitioning (embedded context) may have prevented reading
              // localStorage — verify via caller-provided external source of truth.
              options.fallbackCheck().then((verified) => {
                resolveOnce(verified ?? { success: false, error: 'Authentication cancelled' });
              });
            } else {
              resolveOnce({ success: false, error: 'Authentication cancelled' });
            }
          }
        }, GRACE_CHECK_INTERVAL_MS) as unknown as ReturnType<typeof setTimeout>;
      }
    }, 500);

    // Channel 5: Proactive localStorage poll.
    // GitHub and GitLab set COOP: same-origin, which severs the browsing context
    // group and breaks channels 1-3 and window.close() inside the popup.
    // localStorage is per-origin (not per browsing context group) so direct reads
    // always work regardless of COOP. When we find the result, cleanup() calls
    // popup.close() from the parent — openers can always close their own popups.
    localStoragePollTimer = setInterval(() => {
      const result = tryReadLocalStorage();
      if (result) {
        resolveOnce(result);
      }
    }, 500);

    // Timeout after 5 minutes
    timeoutTimer = setTimeout(() => {
      resolveOnce({
        success: false,
        error: 'Authentication timed out',
      });
    }, 5 * 60 * 1000);
  });
}

/**
 * Open OAuth in a popup and call onSuccess when it completes successfully.
 * Pass options.fallbackCheck to handle embedded contexts where storage
 * partitioning (Chrome 115+) prevents localStorage-based channels from
 * delivering the result — fallbackCheck verifies completion via an external
 * source of truth (e.g., the connections API).
 */
export function openOAuthWithRefresh(authUrl: string, onSuccess: () => void, options?: OAuthOptions): void {
  openOAuthPopup(authUrl, options).then((result) => {
    if (result.success) {
      onSuccess();
    }
  });
}

/**
 * Check if current window is a popup.
 * Uses multiple signals since cross-origin redirects (e.g., Shopify OAuth)
 * can break window.opener and window.name references.
 */
export function isPopupWindow(): boolean {
  const hasOpener = window.opener !== null && window.opener !== window;
  const hasPopupName = window.name === OAUTH_POPUP_NAME;
  return hasOpener || hasPopupName;
}

/**
 * Send OAuth result to parent window and close popup.
 * Uses all available channels to maximize delivery reliability,
 * since cross-origin redirects (e.g., Shopify) break window.opener.
 */
export function sendOAuthResultToParent(result: OAuthResult): void {
  // Method 1: localStorage (parent reads via Channel 5 proactive poll)
  try {
    localStorage.setItem(OAUTH_RESULT_STORAGE_KEY, JSON.stringify(result));
  } catch {
    // localStorage may be unavailable in some contexts
  }

  // Method 2: postMessage (works when window.opener preserved)
  if (window.opener) {
    window.opener.postMessage(
      {
        type: 'CONNECTOR_OAUTH_RESULT',
        payload: result,
      },
      window.location.origin,
    );
  }

  // Method 3: BroadcastChannel (reliable same-origin cross-tab communication)
  try {
    const bc = new BroadcastChannel(OAUTH_POPUP_NAME);
    bc.postMessage({ type: 'CONNECTOR_OAUTH_RESULT', payload: result });
    // Don't call bc.close() — let it be GC'd when popup closes.
  } catch {
    // BroadcastChannel not supported in some browsers
  }

  // Attempt to close popup — may fail silently if COOP severed window.opener.
  // The parent will close via popup.close() when Channel 5 delivers the result.
  setTimeout(() => window.close(), 300);
}
